/**
 * Pipeline column component.
 *
 * Stage name + count badge on left, collapse + add buttons on right.
 * Uses @dnd-kit droppable zone with PipelineCard as the sortable wrapper.
 */

import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowLeft,
  ArrowRight,
  DotsVertical,
  Edit03,
  Plus,
  Trash01,
} from '@untitledui/icons';
import {
  type KeyboardEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button as AriaButton } from 'react-aria-components';

import { Badge } from '@/lib/base/badges/badges';
import { ConfirmationDialog } from '@/lib/base/overlays/confirmation-dialog';
import { Dropdown } from '@/lib/base/overlays/dropdown';
import type { EditorKindDescriptor } from '@/lib/crm/inline-edit/editorKinds';
import { useInlineEdit } from '@/lib/crm/inline-edit/useInlineEdit';
import { STAGE_COLORS } from '@/lib/crm/utils/colors/ColorSelect';
import { hexToRgba } from '@/lib/crm/utils/colors/hexToRgba';
import { Dot } from '@/lib/foundations/dot-icon';
import { cx } from '@/utils/cx';

import type { StageFooterLine } from '../utils/computeStageFooter';
import { useDndContainment } from '../utils/dndContainment';
import { PipelineCard } from './PipelineCard';
import { PIPELINE_PANEL_CLASSES } from './PipelinePanel';
import { StageFooter } from './StageFooter';

/** Stable empty lines reference so columns without a footer don't recompute. */
const EMPTY_FOOTER_LINES: StageFooterLine[] = [];

/** Max length for an inline-edited stage name. Matches existing slideout behavior. */
const STAGE_NAME_MAX_LENGTH = 64;

/** Descriptor for the stage-name rename editor — bare single-line text input. */
const BARE_TEXT_DESCRIPTOR: EditorKindDescriptor = {
  kind: 'bare-text',
  commitTrigger: 'enter-or-blur',
  ownsEnterKey: false,
  autoOpenPicker: false,
  defaultOpen: false,
  overlayLayout: 'row',
};

// =============================================================================
// ColorMenuItem — one swatch row inside the "Edit color" sub-popover.
// Extracted as a sub-component so the per-color onAction callback can use
// useCallback (per FRONTEND_PATTERNS — no inline arrow functions in JSX), and
// so the inline `style={{ backgroundColor }}` is isolated to a small wrapper.
// =============================================================================

interface ColorMenuItemProps {
  color: { value: string; label: string };
  onSelect: (color: string) => void;
}

function ColorMenuItem({ color, onSelect }: ColorMenuItemProps) {
  const handleAction = useCallback(() => {
    onSelect(color.value);
  }, [onSelect, color.value]);
  const dotStyle = useMemo(
    () => ({ backgroundColor: color.value }),
    [color.value]
  );
  return (
    <Dropdown.Item
      id={color.value}
      textValue={color.label}
      onAction={handleAction}
    >
      <span
        aria-hidden='true'
        className='inline-block size-3.5 rounded-full mr-2 shrink-0'
        style={dotStyle}
      />
      {color.label}
    </Dropdown.Item>
  );
}

// Style constants to prevent object recreation on re-renders
const COLLAPSED_WIDTH_STYLE = { width: '56px' } as const;
const EXPANDED_WIDTH_STYLE = { width: '347px' } as const;
const ROTATED_TEXT_STYLE = {
  writingMode: 'vertical-rl',
  transform: 'rotate(180deg)',
} as const;

// Stable input to useSortable for the column drag-handle. Hoisted so the
// reference doesn't change between renders (dnd-kit registrations are stable
// and we don't trigger re-registration on every column re-render).
const COLUMN_SORTABLE_DATA = { type: 'column' } as const;

interface PipelineColumnProps<T> {
  /** Stage configuration */
  stage: { id: string; label: string; color?: string | null };
  /** Items in this column */
  items: T[];
  /** Extract unique ID from an item */
  getItemId: (item: T) => string | number;
  /** Render function for card content */
  renderCard: (item: T, isDragging: boolean) => React.ReactNode;
  /** Called when a card is clicked */
  onItemClick?: (item: T) => void;
  /** Count to display in header badge */
  count?: number;
  /** ID of the item currently being dragged (if any) */
  activeItemId?: string | number | null;
  /** Whether any drag operation is active */
  isDragActive?: boolean;
  /** Callback when user clicks add button — receives stage ID */
  onAddToStage?: (stageId: string) => void;
  /** Whether the column is in collapsed state */
  isCollapsed?: boolean;
  /** Callback to toggle collapsed state — receives stage ID */
  onToggleCollapse?: (stageId: string) => void;
  /** Whether items are still loading */
  loading?: boolean;
  /** Whether viewport is mobile */
  isMobile?: boolean;
  /**
   * Pre-computed calculation-footer lines for this stage. When empty (or
   * omitted) no footer is rendered. Computed by the parent from the items
   * currently visible in this stage.
   */
  footerLines?: StageFooterLine[];
  /**
   * Sentence-case sort label for the transient "Resorting by …" badge that
   * floats over a just-moved card. Null hides the badge.
   */
  resortingLabel?: string | null;
  /**
   * Items currently showing the "Resorting by …" badge. The badge is anchored to
   * the card itself (not the column) so the label sits where the user is
   * looking and costs no layout space.
   */
  resortNoticeItemIds?: ReadonlySet<string | number>;
  /**
   * Items whose badge is fading out. Still rendered — the fade needs the element
   * to stay mounted while `animate-out` plays.
   */
  exitingNoticeIds?: ReadonlySet<string | number>;
  /**
   * ID of the item to flash after it has been re-sorted into a new position,
   * so the eye can reacquire it. Null when nothing is highlighted.
   */
  highlightedItemId?: string | number | null;
  /**
   * Item whose slideout is currently open. Its card gets a persistent ring —
   * distinct from the transient re-sort flash — so it's obvious which card the
   * open panel belongs to.
   */
  openItemId?: string | number | null;
  /**
   * Callback to rename this stage. When provided AND `canEditStage` is true,
   * clicking the title text enables inline editing.
   */
  onRenameStage?: (stageId: string, newName: string) => void;
  /**
   * Callback to move this stage by one position. Direction `'left'` swaps
   * with the previous stage; `'right'` swaps with the next.
   */
  onMoveStage?: (stageId: string, direction: 'left' | 'right') => void;
  /**
   * Callback to insert a new stage adjacent to this one. `'left'` opens the
   * Add Stage modal anchored to the slot just before this stage; `'right'`
   * anchors to just after.
   */
  onInsertStage?: (stageId: string, side: 'left' | 'right') => void;
  /** Callback to change this stage's color (hex value, or null to clear). */
  onRecolorStage?: (stageId: string, color: string | null) => void;
  /**
   * Callback to delete this stage. Only invoked after the user confirms in
   * the destructive ConfirmationDialog.
   */
  onDeleteStage?: (stageId: string) => void;
  /**
   * Whether the current user can edit this stage's structure (rename,
   * reorder, recolor, add). Editing affordances are hidden when false.
   */
  canEditStage?: boolean;
  /**
   * Whether the user can reorder columns by dragging them. When true, the
   * empty interior of the column (below the header, around/below the cards)
   * acts as a drag handle. The title and the cards themselves are NOT drag
   * surfaces — title click still opens inline-rename and cards keep their
   * own drag-into-column behavior.
   *
   * Independent from `canEditStage` so callers can disable column drag
   * without hiding the rename/recolor affordances.
   */
  canReorderColumns?: boolean;
  /**
   * Whether the current user can delete this stage. Independent from
   * canEditStage because delete uses a different permission. Hidden when
   * false.
   */
  canDeleteStage?: boolean;
  /** True if this column is the leftmost in the row (hides Move left). */
  isFirst?: boolean;
  /** True if this column is the rightmost in the row (hides Move right). */
  isLast?: boolean;
  /**
   * True if the pipeline is at the minimum stage count — deleting any one
   * stage would drop it below the minimum, so the Delete item is disabled
   * with an explanatory tooltip. The threshold and the equality check are
   * owned by the parent (PipelineBoard); the column only consumes the
   * resolved boolean. This flag is true whenever the current stage count is
   * strictly below MIN_PIPELINE_STAGES, covering edge cases where the count
   * has already fallen below the threshold.
   */
  isBelowStageMinimum?: boolean;
  /**
   * True while a stage-mutating API call is in flight. Disables the Delete
   * item to prevent rapid back-to-back delete clicks from racing — multiple
   * concurrent mutations on the same pipeline can land at the server in an
   * order whose payloads can't all be reconciled, surfacing as a generic
   * "Couldn't delete stage. Try again." toast.
   */
  isUpdating?: boolean;
  /**
   * True when a board search/filter is active. While filtered, the visible
   * item count is not a reliable emptiness signal, so stage deletion is
   * disabled (a populated deal stage can otherwise look empty). Owned by
   * the parent (PipelineBoard forwards PipelinePage's isFiltered).
   */
  isFiltered?: boolean;
}

/**
 * Skeleton card for loading state.
 */
/** Skeleton card styles matching PipelineCard */
const SKELETON_CARD_CLASSES = `${PIPELINE_PANEL_CLASSES} bg-bg-primary dark:bg-bg-tertiary pt-3 pb-2 px-0`;

function SkeletonCard({ index }: { index: number }) {
  const titleWidth = 55 + ((index * 17) % 35);
  const subtitleWidth = 40 + ((index * 23) % 40);
  return (
    <div className={`${SKELETON_CARD_CLASSES} animate-pulse`}>
      <div className='px-3 space-y-2.5'>
        <div
          className='h-3.5 bg-neutral-200 dark:bg-neutral-700 rounded'
          style={{ width: `${titleWidth}%` }}
        />
        <div
          className='h-3 bg-neutral-200 dark:bg-neutral-700 rounded'
          style={{ width: `${subtitleWidth}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Pipeline column with updated header design.
 *
 * Header layout:
 * ┌──────────────────────────────────┐
 * │ Stage Name  [5]           [<] [+]│
 * └──────────────────────────────────┘
 */
function PipelineColumnInner<T>({
  stage,
  items,
  getItemId,
  renderCard,
  onItemClick,
  count,
  activeItemId,
  isDragActive = false,
  onAddToStage,
  isCollapsed = false,
  onToggleCollapse,
  loading,
  isMobile = false,
  footerLines,
  resortingLabel,
  resortNoticeItemIds,
  exitingNoticeIds,
  highlightedItemId,
  openItemId,
  onRenameStage,
  onMoveStage,
  onInsertStage,
  onRecolorStage,
  onDeleteStage,
  canEditStage = false,
  canDeleteStage = false,
  canReorderColumns = false,
  isFirst = false,
  isLast = false,
  isBelowStageMinimum = false,
  isUpdating = false,
  isFiltered = false,
}: PipelineColumnProps<T>) {
  // Sortable column: registers as both a drop target (for cards being dragged
  // into this column) AND as a draggable sortable item (for column reorder).
  // When `canReorderColumns` is false, the draggable side is disabled but the
  // droppable side remains active so cards can still be dropped into this
  // column. The `data.type` lets the parent's drag handlers branch between
  // card-drop logic and column-reorder logic.
  const sortableDisabled = useMemo(
    () => ({ draggable: !canReorderColumns, droppable: false }),
    [canReorderColumns]
  );
  const {
    setNodeRef,
    isOver,
    attributes,
    listeners,
    transform,
    transition,
    isDragging: isColumnDragging,
  } = useSortable({
    id: stage.id,
    data: COLUMN_SORTABLE_DATA,
    disabled: sortableDisabled,
    // Use dnd-kit's default animateLayoutChanges (skips animation for the
    // just-dropped item via `wasDragging`, but still slides siblings aside
    // during drag). Overriding to () => true caused a "boomerang" — the
    // dropped column visibly animated from its old DOM position to its new
    // one after the optimistic state update landed.
  });

  // Apply transform/transition to the wrapper so columns slide horizontally
  // into their new positions during a column drag. We intentionally skip the
  // transform when this column is collapsed — the collapsed state already
  // animates width and combining the two looks janky.
  const sortableTransformStyle = useMemo<
    React.CSSProperties | undefined
  >(() => {
    if (!transform && !transition) {
      return undefined;
    }
    return {
      transform: CSS.Translate.toString(transform),
      transition,
    };
  }, [transform, transition]);

  const sortableIds = items.map(item => String(getItemId(item)));
  // Fall back to the rendered-card count when the caller-provided count is absent OR
  // drifted negative — the badge must never render a negative value and must match the
  // cards on screen. A valid non-negative count (e.g. a custom entity's numeric `count`
  // property) still wins.
  const displayCount = count != null && count >= 0 ? count : items.length;

  // Empty/loading content
  const emptyContent = loading ? (
    <div role='status' aria-label='Loading items'>
      <div className='space-y-2'>
        {Array.from({ length: 2 + (stage.label.length % 2) }).map((_, i) => (
          // eslint-disable-next-line react/no-array-index-key -- Skeleton loading cards; no item identity
          <SkeletonCard key={`skeleton-${i}`} index={i} />
        ))}
      </div>
    </div>
  ) : (
    // Minimal drop zone — no "No items" text
    <div className='py-4' />
  );

  // Drop zone visual feedback — uses stage color for the outline when available
  const dropZoneStyle = useMemo(() => {
    if (!isOver && !isDragActive) {
      return undefined;
    }
    if (stage.color) {
      // Stage-colored ring via boxShadow (Tailwind ring compiles to this)
      return isOver
        ? { boxShadow: `0 0 0 2px ${hexToRgba(stage.color, 0.3)}` }
        : { boxShadow: `0 0 0 1px ${hexToRgba(stage.color, 0.15)}` };
    }
    return undefined;
  }, [isOver, isDragActive, stage.color]);

  // Fallback ring classes when no stage color is set (with dark mode variants)
  let dropZoneClass = '';
  if (!stage.color) {
    if (isOver) {
      dropZoneClass =
        'ring-2 ring-[var(--color-brand-600)]/20 dark:ring-white/50';
    } else if (isDragActive) {
      dropZoneClass =
        'ring-1 ring-[var(--color-brand-600)]/10 dark:ring-white/20';
    }
  }

  const cardsContent = (
    <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
      {items.length === 0
        ? emptyContent
        : items.map(item => {
            const itemId = getItemId(item);
            const isDragging = activeItemId === itemId;

            return (
              <PipelineCard
                key={itemId}
                id={itemId}
                onClick={() => onItemClick?.(item)}
                isPlaceholder={isDragging}
                isHighlighted={highlightedItemId === itemId}
                resortNotice={
                  resortNoticeItemIds?.has(itemId) ? resortingLabel : undefined
                }
                isResortNoticeExiting={exitingNoticeIds?.has(itemId) ?? false}
                isOpenInSlideout={openItemId === itemId}
              >
                {renderCard(item, isDragging)}
              </PipelineCard>
            );
          })}
    </SortableContext>
  );

  const containment = useDndContainment();

  // Stable handlers to avoid re-creating closures in render
  const handleToggleCollapse = useCallback(() => {
    onToggleCollapse?.(stage.id);
  }, [onToggleCollapse, stage.id]);

  const handleAddToStage = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      containment.onClick(e);
      onAddToStage?.(stage.id);
    },
    [containment, onAddToStage, stage.id]
  );

  // ── Inline-rename UX (via useInlineEdit + useDndContainment) ────────
  // Each column manages its own edit state through the shared headless hook.
  // useDndContainment prevents dnd-kit's KeyboardSensor from intercepting
  // Space/Enter typed into the rename input.
  const isRenameEnabled = canEditStage && Boolean(onRenameStage);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLHeadingElement>(null);

  const onSaveRename = useCallback(
    async (value: unknown) => {
      const trimmed = (value as string).trim();
      if (!trimmed) {
        throw new Error('Stage name cannot be empty.');
      }
      onRenameStage?.(stage.id, trimmed);
    },
    [onRenameStage, stage.id]
  );

  const {
    isEditing,
    draft,
    errorMessage,
    startEdit,
    cancelEdit,
    setDraft,
    handleKeyDown: inlineEditKeyDown,
    handleBlur: inlineEditHandleBlur,
  } = useInlineEdit({
    descriptor: BARE_TEXT_DESCRIPTOR,
    initialValue: stage.label,
    onSave: onSaveRename,
    containerRef,
    triggerRef,
  });

  // Focus + select-all when entering edit mode.
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleTitleClick = useCallback(
    (e: React.MouseEvent<HTMLHeadingElement>) => {
      containment.onClick(e);
      if (!isRenameEnabled) {
        return;
      }
      startEdit();
    },
    [containment, isRenameEnabled, startEdit]
  );

  const handleEditChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDraft(e.target.value);
    },
    [setDraft]
  );

  // Blur on empty: cancel (no error-state lock-in); blur on non-empty: commit.
  const handleEditBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if ((draft as string).trim().length === 0) {
        cancelEdit();
        return;
      }
      inlineEditHandleBlur(e);
    },
    [draft, cancelEdit, inlineEditHandleBlur]
  );

  // Stop propagation (dnd-kit containment) then handle Escape/Enter via hook.
  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      containment.onKeyDown(e);
      inlineEditKeyDown(e);
    },
    [containment, inlineEditKeyDown]
  );

  // Keyboard support for the click-to-edit title — Space/Enter enters edit mode.
  const handleTitleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLHeadingElement>) => {
      containment.onKeyDown(e);
      if (!isRenameEnabled) {
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        startEdit();
      }
    },
    [containment, isRenameEnabled, startEdit]
  );

  // ── Move / insert / delete / recolor handlers ─────────────────────────
  const handleMoveLeft = useCallback(() => {
    onMoveStage?.(stage.id, 'left');
  }, [onMoveStage, stage.id]);

  const handleMoveRight = useCallback(() => {
    onMoveStage?.(stage.id, 'right');
  }, [onMoveStage, stage.id]);

  const handleInsertLeft = useCallback(() => {
    onInsertStage?.(stage.id, 'left');
  }, [onInsertStage, stage.id]);

  const handleInsertRight = useCallback(() => {
    onInsertStage?.(stage.id, 'right');
  }, [onInsertStage, stage.id]);

  const handleRecolor = useCallback(
    (color: string) => {
      onRecolorStage?.(stage.id, color);
    },
    [onRecolorStage, stage.id]
  );

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const handleRequestDelete = useCallback(() => {
    setIsDeleteConfirmOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    onDeleteStage?.(stage.id);
    setIsDeleteConfirmOpen(false);
  }, [onDeleteStage, stage.id]);

  const handleCancelDelete = useCallback(() => {
    setIsDeleteConfirmOpen(false);
  }, []);

  // Disabled state for the delete menu item. Three reasons it can be
  // disabled, in priority order so the tooltip explains the most relevant:
  //   1. An update is already in flight — prevent racing mutations.
  //   2. Pipeline is at the minimum stage count — a pipeline must keep at
  //      least 3 stages to be a useful workflow.
  //   3. The stage still has items — they must be moved/removed first.
  const canActuallyDelete =
    canDeleteStage &&
    displayCount === 0 &&
    !isBelowStageMinimum &&
    !isUpdating &&
    !isFiltered;
  const deleteDisabledTooltip = (() => {
    if (!canDeleteStage) {
      return undefined;
    }
    if (isUpdating) {
      return 'Saving – try again in a moment.';
    }
    if (isBelowStageMinimum) {
      return 'A pipeline must have at least 3 stages.';
    }
    if (isFiltered) {
      return 'Clear search and filters to delete a stage.';
    }
    if (displayCount > 0) {
      return 'Move or remove items in this stage to delete it.';
    }
    return undefined;
  })();

  // Tooltips for the four edge-disabled items (Move left/right and Add
  // stage left/right). Same wording for both verbs at the same edge — the
  // dropdown label clarifies the action; the tooltip just explains the
  // disabled state.
  const leftEdgeTooltip = isFirst ? 'Already the leftmost stage.' : undefined;
  const rightEdgeTooltip = isLast ? 'Already the rightmost stage.' : undefined;

  // Whether to render any of the new "edit" dropdown items.
  const showEditMenuItems =
    canEditStage && Boolean(onMoveStage || onRecolorStage || onInsertStage);
  const showInsertMenuItems = canEditStage && Boolean(onInsertStage);
  const showDeleteMenuItem = canDeleteStage && Boolean(onDeleteStage);

  // Whether the actions dropdown should render at all. The dropdown is shown
  // on both mobile and desktop, but the Collapse item only renders on
  // desktop (the mobile layout has no collapsed-column state to flip). So
  // the dropdown can still render on mobile when there are edit/delete
  // items to show.
  const hasDropdownItems =
    (!isMobile && Boolean(onToggleCollapse)) ||
    showEditMenuItems ||
    showDeleteMenuItem;

  const colorDotStyle = useMemo(
    () => (stage.color ? { color: stage.color } : undefined),
    [stage.color]
  );

  /** Pill styles: lightened stage color as bg, stage color as border */
  const stagePillStyle = useMemo(
    () =>
      stage.color
        ? {
            backgroundColor: hexToRgba(stage.color, 0.08),
            borderColor: hexToRgba(stage.color, 0.3),
          }
        : undefined,
    [stage.color]
  );

  // --- Column header (shared between mobile and desktop) ---
  // Wrapped in a card matching pipeline card border/radius/shadow
  const columnHeader = (
    <div
      className={cx(
        PIPELINE_PANEL_CLASSES,
        // `mx-3 mt-3` (not `m-3`): the header only insets its own top/side
        // edges. The gap below the header now lives as the scroll container's
        // top padding (`p-3` there, see below) so the first card's
        // selection/focus outline paints inside the scroll box instead of
        // being clipped at its top edge.
        // `px-3 py-2` is the header card's own internal padding — unrelated.
        'group/header flex items-center gap-2 flex-shrink-0 mx-3 mt-3 px-3 py-2 bg-bg-primary'
      )}
    >
      {/* Stage name pill — colored bg/border with dot */}
      <div
        ref={containerRef}
        className={cx(
          'flex items-center gap-1.5 px-2 py-0.5 rounded-md text-sm font-semibold min-w-0 overflow-hidden',
          stagePillStyle && 'border'
        )}
        style={stagePillStyle}
      >
        {colorDotStyle && (
          <Dot size='sm' style={colorDotStyle} aria-hidden='true' />
        )}
        {isEditing ? (
          <div className='min-w-0 flex-1'>
            <input
              ref={inputRef}
              type='text'
              value={draft as string}
              onChange={handleEditChange}
              onBlur={handleEditBlur}
              onKeyDown={handleInputKeyDown}
              onClick={containment.onClick}
              onPointerDown={containment.onPointerDown}
              maxLength={STAGE_NAME_MAX_LENGTH}
              aria-label={`Rename ${stage.label} stage`}
              aria-invalid={errorMessage !== null || undefined}
              className={cx(
                'min-w-0 max-w-full bg-transparent text-sm font-semibold outline-none p-0 m-0 border-0',
                'text-[var(--color-text-primary)]',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring',
                errorMessage !== null && 'text-error-600'
              )}
            />
            {errorMessage && (
              <p
                role='alert'
                className='mt-1 text-xs text-[var(--color-text-error-primary)]'
              >
                {errorMessage}
              </p>
            )}
          </div>
        ) : (
          <h3
            ref={triggerRef}
            className={cx(
              'text-[var(--color-text-primary)] truncate',
              isRenameEnabled &&
                'cursor-text hover:text-[var(--color-text-secondary)] transition-colors',
              isRenameEnabled &&
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring rounded-sm'
            )}
            title={stage.label}
            onClick={isRenameEnabled ? handleTitleClick : undefined}
            onKeyDown={isRenameEnabled ? handleTitleKeyDown : undefined}
            onPointerDown={
              isRenameEnabled ? containment.onPointerDown : undefined
            }
            role={isRenameEnabled ? 'button' : undefined}
            tabIndex={isRenameEnabled ? 0 : undefined}
            aria-label={
              isRenameEnabled ? `Rename ${stage.label} stage` : undefined
            }
            data-testid='pipeline-stage-title'
            data-component='pipeline-stage-title'
            data-instance={stage.id}
          >
            {stage.label}
          </h3>
        )}
        {isRenameEnabled && !isEditing && (
          // Purely decorative discoverability hint for the click-to-rename
          // title (the h3 above is the interactive rename control). Revealed
          // on header hover; pointer-events-none so clicks pass through.
          <Edit03
            aria-hidden='true'
            // Hidden entirely (not just transparent) until the header is
            // hovered, so no empty icon box or gap is reserved beside the name
            // when idle. pointer-events-none — clicks pass through to the title.
            className='hidden group-hover/header:block shrink-0 size-3.5 text-[var(--color-text-tertiary)] pointer-events-none'
            data-testid='pipeline-stage-rename-pencil'
          />
        )}
      </div>

      <Badge type='color' color='gray' size='sm' className='flex-shrink-0'>
        <span className='text-xs'>{displayCount}</span>
      </Badge>

      {/* Spacer pushes action buttons to the right */}
      <div className='flex-1' />

      {/* Stage actions menu — visible on both mobile and desktop. The
          mobile layout doesn't support collapsed columns, so the Collapse
          item itself is gated on `!isMobile`. */}
      {hasDropdownItems && (
        <Dropdown.Root>
          <AriaButton
            aria-label={`${stage.label} column actions`}
            className='p-1 rounded hover:bg-[var(--color-bg-tertiary)] text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)] transition-colors cursor-pointer flex-shrink-0'
          >
            <DotsVertical className='size-4' />
          </AriaButton>
          <Dropdown.Popover placement='bottom end'>
            <Dropdown.Menu>
              {/* Section 1: Collapse (desktop only) + Change color */}
              {!isMobile && onToggleCollapse && (
                <Dropdown.Item
                  id='collapse'
                  textValue='Collapse'
                  onAction={handleToggleCollapse}
                >
                  Collapse
                </Dropdown.Item>
              )}
              {showEditMenuItems && onRecolorStage && (
                <Dropdown.SubmenuTrigger label='Change color'>
                  {/* Placement is chosen automatically by Dropdown.SubmenuTrigger
                      based on available space — side-popover when there's
                      horizontal room, falls back below the trigger when not. */}
                  <Dropdown.Popover>
                    <Dropdown.Menu>
                      {STAGE_COLORS.map(c => (
                        <ColorMenuItem
                          key={c.value}
                          color={c}
                          onSelect={handleRecolor}
                        />
                      ))}
                    </Dropdown.Menu>
                  </Dropdown.Popover>
                </Dropdown.SubmenuTrigger>
              )}

              {/* Section 2: Move left + Move right (disabled at row ends) */}
              {showEditMenuItems && onMoveStage && (
                <>
                  <Dropdown.Separator />
                  <Dropdown.Item
                    id='move-left'
                    label='Move left'
                    icon={ArrowLeft}
                    isDisabled={isFirst}
                    disabledTooltip={leftEdgeTooltip}
                    onAction={handleMoveLeft}
                  />
                  <Dropdown.Item
                    id='move-right'
                    label='Move right'
                    icon={ArrowRight}
                    isDisabled={isLast}
                    disabledTooltip={rightEdgeTooltip}
                    onAction={handleMoveRight}
                  />
                </>
              )}

              {/* Section 3: Add stage left + Add stage right (disabled at
                  row ends, mirroring Move left / Move right). Both items
                  open the AddStageModal anchored to this column's slot. */}
              {showInsertMenuItems && (
                <>
                  <Dropdown.Separator />
                  <Dropdown.Item
                    id='add-stage-left'
                    label='Add stage left'
                    icon={Plus}
                    isDisabled={isFirst}
                    disabledTooltip={leftEdgeTooltip}
                    onAction={handleInsertLeft}
                  />
                  <Dropdown.Item
                    id='add-stage-right'
                    label='Add stage right'
                    icon={Plus}
                    isDisabled={isLast}
                    disabledTooltip={rightEdgeTooltip}
                    onAction={handleInsertRight}
                  />
                </>
              )}

              {/* Section 4: Delete (destructive, disabled when stage has items) */}
              {showDeleteMenuItem && (
                <>
                  <Dropdown.Separator />
                  <Dropdown.Item
                    id='delete'
                    label='Delete stage'
                    icon={Trash01}
                    isDisabled={!canActuallyDelete}
                    disabledTooltip={deleteDisabledTooltip}
                    onAction={handleRequestDelete}
                  />
                </>
              )}
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown.Root>
      )}

      {/* Add button */}
      {onAddToStage && (
        <button
          type='button'
          onClick={handleAddToStage}
          onPointerDown={containment.onPointerDown}
          aria-label={`Add item to ${stage.label} stage`}
          className='p-1 rounded hover:bg-[var(--color-bg-tertiary)] text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)] transition-colors flex-shrink-0'
        >
          <Plus className='size-4' />
        </button>
      )}
    </div>
  );

  // Merge width + drop-zone outline for desktop column style.
  // Sortable transform/transition is added when this column is sliding into a
  // new position during a column drag (own drag or sibling drag pushing it
  // aside). When collapsed we skip the sortable transform — the width-change
  // animation already animates layout and stacking the two looks janky.
  const desktopColumnStyle = useMemo(
    () => ({
      ...(isCollapsed ? COLLAPSED_WIDTH_STYLE : EXPANDED_WIDTH_STYLE),
      ...(isCollapsed ? undefined : sortableTransformStyle),
      ...dropZoneStyle,
    }),
    [isCollapsed, sortableTransformStyle, dropZoneStyle]
  );

  // Mobile layout: include sortable transform so reorder works on mobile too
  // (the mobile carousel is a horizontal scroll list — drag-reorder still
  // applies). Drop-zone outline is layered on top.
  const mobileColumnStyle = useMemo(
    () => ({
      ...sortableTransformStyle,
      ...dropZoneStyle,
    }),
    [sortableTransformStyle, dropZoneStyle]
  );

  // Drag-handle props for the inner content wrapper (covers header + cards-
  // area). Listeners and a11y attributes attach here, NOT the outer column
  // wrapper, so:
  //   - Outer wrapper keeps role='region' (a11y-correct labeled section).
  //   - Inner wrapper is the sortable drag handle (any space inside the
  //     stage card initiates column drag — including around the title pill,
  //     around the dropdown / + buttons, and below the cards).
  //   - Title click still triggers inline-rename: the title's own onClick
  //     fires on plain clicks; drag only activates after 8px pointer
  //     movement (MouseSensor activationConstraint).
  //   - Dropdown / + add-card buttons still respond to clicks for the same
  //     reason.
  //   - Cards keep their own drag-into-column behavior: each card has its
  //     own useSortable listeners on a deeper element; dnd-kit picks the
  //     closest draggable on pointerdown.
  // When draggable is disabled (no permission or onReorderStages omitted),
  // `listeners` is undefined so spreading is a no-op and the cursor stays
  // default.
  const dragHandleProps = canReorderColumns ? listeners : undefined;
  const dragHandleAriaProps = canReorderColumns ? attributes : undefined;
  const dragHandleClass = canReorderColumns
    ? 'cursor-grab active:cursor-grabbing'
    : '';

  // Delete-confirmation dialog. Rendered once for both mobile and desktop;
  // it's a portal-backed modal so its DOM position doesn't matter.
  const deleteConfirmDialog = showDeleteMenuItem ? (
    <ConfirmationDialog
      isOpen={isDeleteConfirmOpen}
      title={`Delete stage "${stage.label}"?`}
      description='This cannot be undone.'
      confirmLabel='Delete'
      cancelLabel='Cancel'
      variant='destructive'
      onConfirm={handleConfirmDelete}
      onCancel={handleCancelDelete}
    />
  ) : null;

  // --- Mobile layout ---
  if (isMobile) {
    return (
      <>
        <div
          ref={setNodeRef}
          style={mobileColumnStyle}
          className={cx(
            PIPELINE_PANEL_CLASSES,
            'bg-bg-secondary dark:bg-bg-secondary w-[85vw] flex-shrink-0 flex flex-col h-full snap-center dark:ring-1 dark:ring-white/10',
            'transition-[background-color,box-shadow] duration-200 motion-reduce:transition-none',
            isColumnDragging && 'opacity-40',
            dropZoneClass
          )}
          role='region'
          aria-label={`${stage.label} column`}
        >
          {/* Drag-handle wrapper: holds the column padding so the entire
              stage-card interior — including the gutter around the header
              card — is a drag surface. Title / dropdown / + buttons are
              still clickable thanks to the 8px sensor activation. */}
          <div
            {...dragHandleProps}
            {...dragHandleAriaProps}
            className={cx(
              'flex flex-col h-full min-h-0',
              canReorderColumns && 'touch-manipulation',
              dragHandleClass
            )}
          >
            {columnHeader}
            <div
              // `p-3`: the gutter lives INSIDE the scroll area so the
              // scrollbar tracks the column edge instead of floating inset.
              // The top padding gives the first card's selection/focus outline
              // room to paint without being clipped by the scroll container's
              // top edge; the header supplies only its own top/side insets.
              className='flex-1 overflow-y-auto flex flex-col min-h-0 p-3'
            >
              <div className='space-y-2 flex-shrink-0'>{cardsContent}</div>
              <div className='flex-1 min-h-[80px]' />
            </div>
            <StageFooter lines={footerLines ?? EMPTY_FOOTER_LINES} />
          </div>
        </div>
        {deleteConfirmDialog}
      </>
    );
  }

  // --- Desktop layout ---
  return (
    <>
      <div
        ref={setNodeRef}
        style={desktopColumnStyle}
        className={cx(
          PIPELINE_PANEL_CLASSES,
          'bg-bg-secondary dark:bg-bg-secondary flex-shrink-0 flex flex-col h-full dark:ring-1 dark:ring-white/10',
          'transition-[width,background-color,box-shadow] duration-200 will-change-[width] motion-reduce:transition-none',
          isColumnDragging && 'opacity-40',
          dropZoneClass
        )}
        role='region'
        aria-label={`${stage.label} column`}
        aria-expanded={!isCollapsed}
      >
        {isCollapsed ? (
          <button
            type='button'
            onClick={handleToggleCollapse}
            className='flex-1 flex flex-col items-center justify-center cursor-pointer hover:bg-[var(--color-bg-tertiary)] transition-colors rounded-lg p-3'
            aria-label={`Expand ${stage.label} column`}
            aria-expanded={!isCollapsed}
          >
            <span
              className='text-sm font-semibold text-[var(--color-text-secondary)] whitespace-nowrap'
              style={ROTATED_TEXT_STYLE}
              aria-hidden='true'
            >
              {stage.label}
            </span>
            <span className='mt-2 text-xs text-[var(--color-text-tertiary)]'>
              {displayCount}
            </span>
          </button>
        ) : (
          // Drag-handle wrapper: holds the column padding so the entire
          // stage-card interior — including the gutter around the header
          // card — is a drag surface. The collapsed branch above is
          // intentionally NOT a drag surface — collapsed columns are
          // click-to-expand, and Move-left/right in the dropdown is the
          // reorder fallback while collapsed.
          <div
            {...dragHandleProps}
            {...dragHandleAriaProps}
            className={cx('flex flex-col h-full w-full', dragHandleClass)}
          >
            {columnHeader}
            <div
              // `p-3`: the gutter lives INSIDE the scroll area so the
              // scrollbar tracks the column edge instead of floating inset.
              // The top padding gives the first card's selection/focus outline
              // room to paint without being clipped by the scroll container's
              // top edge; the header supplies only its own top/side insets.
              className='flex-1 overflow-y-auto flex flex-col min-h-0 p-3'
            >
              <div className='space-y-2 flex-shrink-0'>{cardsContent}</div>
              <div className='flex-1 min-h-[80px]' />
            </div>
            <StageFooter lines={footerLines ?? EMPTY_FOOTER_LINES} />
          </div>
        )}
      </div>
      {deleteConfirmDialog}
    </>
  );
}

/** Prop keys compared by Set-membership instead of `Object.is` — see below. */
const SET_MEMBERSHIP_PROP_KEYS = [
  'resortNoticeItemIds',
  'exitingNoticeIds',
] as const;

function areSetsEqualByMembership(
  a: ReadonlySet<unknown> | undefined,
  b: ReadonlySet<unknown> | undefined
): boolean {
  if (a === b) {
    return true;
  }
  const aSize = a?.size ?? 0;
  const bSize = b?.size ?? 0;
  if (aSize !== bSize) {
    return false;
  }
  if (aSize === 0) {
    return true;
  }
  for (const value of a as ReadonlySet<unknown>) {
    if (!(b as ReadonlySet<unknown>).has(value)) {
      return false;
    }
  }
  return true;
}

/**
 * Custom `memo` comparator for `PipelineColumn`.
 *
 * `resortNoticeItemIds` and `exitingNoticeIds` are Sets recomputed (via
 * `groupIdsByStage`) whenever ANY item on the board gets a resort notice —
 * even for stages whose membership didn't change, a fresh (but
 * content-equal) Set object is allocated. Default `memo` shallow-compare
 * uses reference equality, so those props would look "changed" every time
 * and defeat memoization for every column simultaneously. This comparator
 * treats the two Set props as equal when their membership matches
 * (order-independent — Set iteration order is insertion order and can
 * legitimately differ between an old and new equal-content Set) and falls
 * back to `Object.is` for every other prop, matching React's own default
 * shallow-compare semantics.
 *
 * Per the `memo` contract: return `true` to SKIP a re-render (props
 * equivalent), `false` to re-render (props differ) — the inverse of a
 * typical `isEqual` check.
 */
// eslint-disable-next-line react-refresh/only-export-components -- memo comparator co-located with the component it compares, exported for direct unit testing
export function arePipelineColumnPropsEqual<T>(
  prevProps: PipelineColumnProps<T>,
  nextProps: PipelineColumnProps<T>
): boolean {
  const prevKeys = Object.keys(prevProps) as (keyof PipelineColumnProps<T>)[];
  const nextKeys = Object.keys(nextProps) as (keyof PipelineColumnProps<T>)[];
  if (prevKeys.length !== nextKeys.length) {
    return false;
  }

  for (const key of prevKeys) {
    if ((SET_MEMBERSHIP_PROP_KEYS as readonly string[]).includes(key)) {
      if (
        !areSetsEqualByMembership(
          prevProps[key] as ReadonlySet<unknown> | undefined,
          nextProps[key] as ReadonlySet<unknown> | undefined
        )
      ) {
        return false;
      }
      continue;
    }
    if (!Object.is(prevProps[key], nextProps[key])) {
      return false;
    }
  }

  return true;
}

// Memoize to prevent re-renders during drag operations. Custom comparator
// (see arePipelineColumnPropsEqual) treats resortNoticeItemIds/exitingNoticeIds
// as equal by Set membership rather than reference, since those props are
// recomputed board-wide on every resort notice change.
// Generic components can't be directly wrapped in memo, so we cast.
const Memoized = memo(PipelineColumnInner, arePipelineColumnPropsEqual);
export const PipelineColumn = Memoized as typeof PipelineColumnInner;
